import type {
    AccountingPeriod,
    AccountingPeriodStatus,
    ClosePreview,
    FiscalYear,
    FiscalYearStatus,
    JournalEntry,
    JournalLine,
} from '@/api/models';
import { decimalGelOf } from '../journal/journalMapping';

/**
 * Wire ⇄ domain mapping for fiscal years, accounting periods and the year-end
 * close (bos-api).
 *
 * **This is the only file that knows the server's field names.** Everything
 * downstream — the service, the query hooks, the page hook, the table, the
 * dialogs — sees the domain shapes in `@/api/models/Periods`. Same seam, and the
 * same reason, as `accountsMapping`, `journalMapping`, `statementsMapping` and
 * `vatMapping`. This is the fifth integration and the fifth to find the
 * frontend-authored contract diverging in nearly every field.
 *
 * Measured against `/openapi/v1.json` and live responses on 2026-07-31.
 *
 * ## Divergence table
 *
 * | domain (as the mock had it) | wire |
 * | --- | --- |
 * | `GET /fiscal-years` → `{ items }` | a **bare array** of `FiscalYearResponse`, no envelope, no paging |
 * | `GET /fiscal-years/{id}/periods` | **does not exist** — periods arrive nested on the year (`periods[]`) |
 * | fiscal year addressed by `id` (`'FY2026'`) | addressed by the **integer year** in the path; the payload's `id` is a uuid used in no route |
 * | period addressed by `id` (`'2026-06'`) | addressed by **`{year}/periods/{periodNumber}`**; the payload's `id` is a uuid used in no route |
 * | `FiscalYear.label` | *absent* — derived here from `year` |
 * | *(absent)* | `closedAt` / `closedBy` on both the year and each period |
 * | `FiscalYearStatus = OPEN \| CLOSED` | one `PeriodStatus` enum for both levels: **`OPEN \| CLOSED \| LOCKED`** |
 * | `POST /periods/{id}/close` → `{ period, fiscalYear }` | `POST /fiscal-years/{y}/periods/{n}/close` → the **whole `FiscalYearResponse`** |
 * | `POST /fiscal-years/{id}/close` → `{ fiscalYear, closingEntry }` | → a bare `FiscalYearResponse`; the entry must be fetched by `closingEntryId` |
 * | `POST /fiscal-years/{id}/reopen` → `{ fiscalYear, reversalEntries }` | → a bare `FiscalYearResponse` with **both entry ids cleared to `null`** |
 * | `POST /fiscal-years/{id}/close/preview` | **`GET`**, and it takes a `locale` query param |
 * | preview → `ClosePreview` (an entry) | `ClosingEntryPreviewResponse`: flat `lines[]` + `totalDebitTetri` / `totalCreditTetri`, **no entry, no target account, no result figure** |
 * | preview line `debit` / `credit` as decimal strings | JSON **numbers** (`debit: 82500`), alongside the authoritative `debitTetri` |
 * | `POST /fiscal-years/{id}/rollover/preview` | **does not exist** — `404`. There is no rollover dry run at all |
 * | `AccountingPeriod.entryCount` / `draftCount` / `postedTotalTetri` | **not served anywhere** — aggregated here from the journal |
 * | `GET /periods/status?date=` | **does not exist** — postability is resolved from the fiscal-year read |
 * | refusals carry `errors.reason` | refusals are `{ error: "<English prose>" }` with **no `code` and no `errors`** — re-swept 2026-07-31 across nine refusals, still true of every one |
 *
 * ## What the wire does that the client did not expect
 *
 * 1. **Periods must be closed in order.** `POST …/periods/6/close` with 2–5 open
 *    is refused: *"Close earlier period(s) first: 2, 3, 4, 5."* The client's own
 *    rule allowed any OPEN period to close. `periodsMeta.getPeriodActions` now
 *    carries the sequential gate so the refusal is pre-empted with a reason
 *    rather than surfaced as prose after the click.
 * 2. **Reopen is gated too, in the opposite direction — newest first.**
 *    ⚠️ **This changed.** It used to be ungated: reopening period 1 under a
 *    closed period 2 succeeded and left a hole, and both this file and the
 *    screen were built on that asymmetry. Re-measured 2026-07-31 with January–May
 *    closed, the server now refuses with the exact mirror of the close message —
 *    reopening 1 gave *"Reopen later period(s) first: 2, 3, 4, 5."*, reopening 3
 *    gave *"…: 4, 5."*, and reopening 5 succeeded, after which 4 became the
 *    reopenable one. So exactly one period is reopenable at a time: the newest
 *    closed one. `periodsMeta.getLaterClosedCounts` carries the mirror gate.
 *
 *    The refusal enumerates later **`CLOSED`** periods only — later `OPEN` ones
 *    are excluded, verified directly. Whether a later `LOCKED` period also
 *    blocks is **not measured**: producing one costs a permanent `/lock` with no
 *    inverse anywhere in the API, so the dev tenant was not spent on it. The
 *    client counts `CLOSED` only; see `getPeriodActions` for why that asymmetry
 *    is the safe direction to be wrong in.
 * 3. **A year close does not lock its periods.** They stay `CLOSED`, not
 *    `LOCKED` — `LOCKED` is reachable only through the separate `/lock` paths,
 *    which have no inverse and which this client never calls.
 * 4. **The rollover posts into the *next* year.** It is dated `{year+1}-01-01`
 *    and numbered in that year's sequence (`JE-2027-0001`), and it posts even
 *    when that fiscal year has not been generated.
 * 5. **`{ date }` on the close body is honoured, and that is a trap.** The
 *    preview is fixed at the year's `endDate`; sending a different `date` posts
 *    the CLOSING entry on that date instead, so the commit stops matching the
 *    approved preview. The service sends **no body at all** — which also
 *    guarantees no `source` can travel.
 * 6. **`locale` on the preview is honoured** (`accountName` comes back
 *    Georgian by default, English for `locale=en`); an unrecognised value falls
 *    back to `ka` rather than erroring.
 *
 * ## The one that is a server defect, and is deliberately not worked around
 *
 * **After a reopen, the P&L for the reopened year double-counts.** The P&L
 * excludes entries with `source: CLOSING`, but the reversal the reopen posts
 * carries `source: REVERSAL` — so the close is excluded while its undo is
 * included, re-booking every revenue and expense account a second time.
 * Measured: every P&L line exactly doubled (revenue ₾84,500 → ₾169,000) while
 * the balance sheet stayed correct.
 *
 * This client does not correct it. Recomputing or filtering statement figures
 * here would put a second opinion in front of the one number the statements
 * exist to state, and would hide a real backend bug behind a plausible screen.
 * The fix belongs in the API: exclude the reversal of a CLOSING entry from the
 * P&L, or stamp it `CLOSING` rather than `REVERSAL`.
 */

// ── wire shapes ─────────────────────────────────────────────────────────────

export type AccountingPeriodWire = {
    id?: string;
    periodNumber?: number;
    startDate?: string;
    endDate?: string;
    status?: string;
    closedAt?: string | null;
    closedBy?: string | null;
};

export type FiscalYearWire = {
    id?: string;
    year?: number;
    startDate?: string;
    endDate?: string;
    status?: string;
    closingEntryId?: string | null;
    rolloverEntryId?: string | null;
    closedAt?: string | null;
    closedBy?: string | null;
    periods?: AccountingPeriodWire[];
};

export type ClosingEntryPreviewLineWire = {
    accountId?: string;
    accountCode?: string;
    accountName?: string;
    debitTetri?: number;
    creditTetri?: number;
};

export type ClosingEntryPreviewWire = {
    year?: number;
    date?: string;
    lines?: ClosingEntryPreviewLineWire[];
    totalDebitTetri?: number;
    totalCreditTetri?: number;
};

// ── identity ────────────────────────────────────────────────────────────────

/**
 * The composite that addresses a period, `'{year}:{periodNumber}'`.
 *
 * Neither half addresses a period on its own, and the server's uuid addresses
 * nothing at all, so this is what travels as `AccountingPeriod.id`.
 */
export const periodId = (year: number, periodNumber: number): string =>
    `${year}:${periodNumber}`;

/** Split a {@link periodId} back apart, or `null` if it is not one. */
export const parsePeriodId = (
    id: string,
): { year: number; periodNumber: number } | null => {
    const [rawYear, rawNumber] = id.split(':');
    const year = Number(rawYear);
    const periodNumber = Number(rawNumber);
    return Number.isInteger(year) && Number.isInteger(periodNumber)
        ? { year, periodNumber }
        : null;
};

/**
 * The year a `FiscalYear.id` addresses, or `null`.
 *
 * `id` is the year as a string precisely so this is total and cheap; the
 * server's uuid would address nothing.
 */
export const parseFiscalYearId = (id: string): number | null => {
    const year = Number(id);
    return Number.isInteger(year) ? year : null;
};

// ── status ──────────────────────────────────────────────────────────────────

const PERIOD_STATUSES: readonly string[] = ['OPEN', 'CLOSED', 'LOCKED'];

/**
 * One server enum serves both levels. An unrecognised value maps to `CLOSED`
 * rather than `OPEN`: the two states differ by whether posting is allowed, and
 * guessing "open" would offer a Post the ledger then refuses — the failure the
 * whole period gate exists to prevent.
 */
const mapStatus = (value: unknown): AccountingPeriodStatus =>
    typeof value === 'string' && PERIOD_STATUSES.includes(value)
        ? (value as AccountingPeriodStatus)
        : 'CLOSED';

// ── reads ───────────────────────────────────────────────────────────────────

/** `YYYY-MM` of an ISO `YYYY-MM-DD`. */
const yearMonthOf = (date: string): string => date.slice(0, 7);

/**
 * Zeroed activity, used until the journal aggregation lands.
 *
 * Zero here means *not counted yet*, not *nothing there* — the service always
 * folds {@link summarisePeriodEntries} over the result before returning it.
 */
const NO_ACTIVITY = { entryCount: 0, draftCount: 0, postedTotalTetri: 0 };

export const mapFiscalYear = (wire: FiscalYearWire): FiscalYear | null => {
    const year = wire.year;
    if (typeof year !== 'number' || !Number.isInteger(year)) {
        return null;
    }
    return {
        id: String(year),
        year,
        serverId: wire.id ?? '',
        label: String(year),
        startDate: wire.startDate ?? '',
        endDate: wire.endDate ?? '',
        status: mapStatus(wire.status) as FiscalYearStatus,
        closingEntryId: wire.closingEntryId ?? null,
        rolloverEntryId: wire.rolloverEntryId ?? null,
        closedAt: wire.closedAt ?? null,
        closedBy: wire.closedBy ?? null,
    };
};

/**
 * A year that cannot be mapped is **dropped, not guessed at** — the year drives
 * every path on this screen, and one built from a fabricated number would close
 * the wrong books.
 */
export const mapFiscalYears = (wire: unknown): FiscalYear[] =>
    (Array.isArray(wire) ? (wire as FiscalYearWire[]) : [])
        .map(mapFiscalYear)
        .filter((year): year is FiscalYear => year !== null)
        // Newest first — the selector defaults to the top entry, and the server
        // returns them oldest-first.
        .sort((a, b) => b.year - a.year);

export const mapPeriods = (wire: FiscalYearWire): AccountingPeriod[] => {
    const year = wire.year;
    if (typeof year !== 'number') {
        return [];
    }
    return (wire.periods ?? [])
        .filter((period) => typeof period.periodNumber === 'number')
        .map((period) => {
            const startDate = period.startDate ?? '';
            return {
                id: periodId(year, period.periodNumber!),
                serverId: period.id ?? '',
                fiscalYearId: String(year),
                fiscalYear: year,
                yearMonth: yearMonthOf(startDate),
                sequence: period.periodNumber!,
                startDate,
                endDate: period.endDate ?? '',
                status: mapStatus(period.status),
                ...NO_ACTIVITY,
                closedAt: period.closedAt ?? null,
                closedBy: period.closedBy ?? null,
            };
        })
        .sort((a, b) => a.sequence - b.sequence);
};

// ── journal aggregation (the API serves none of this) ────────────────────────

/** The journal-list fields this aggregation depends on. */
export type PeriodEntryWire = {
    date?: string;
    status?: string;
    amountTetri?: number;
};

/**
 * Fold a year's journal entries into per-period counts and totals.
 *
 * No period endpoint carries any of this, and the screen's whole point is to
 * show what a month contains before you seal it — so it is aggregated here from
 * `GET /ledger/journal-entries` over the year's range, bucketed by the periods'
 * own inclusive date bounds.
 *
 * Bucketing is by **date containment**, not by month arithmetic: the periods are
 * whatever the server says they are, and a fiscal year need not start in
 * January. An entry outside every period is ignored rather than forced into the
 * nearest one.
 */
export const summarisePeriodEntries = (
    periods: AccountingPeriod[],
    entries: readonly PeriodEntryWire[],
): AccountingPeriod[] =>
    periods.map((period) => {
        const inside = entries.filter(
            (entry) =>
                typeof entry.date === 'string' &&
                entry.date >= period.startDate &&
                entry.date <= period.endDate,
        );
        return {
            ...period,
            entryCount: inside.length,
            draftCount: inside.filter((entry) => entry.status === 'DRAFT')
                .length,
            postedTotalTetri: inside
                .filter((entry) => entry.status === 'POSTED')
                .reduce((sum, entry) => sum + (entry.amountTetri ?? 0), 0),
        };
    });

// ── the close preview ───────────────────────────────────────────────────────

/**
 * Which line of the preview is the balancing one — the result being moved.
 *
 * The server does not label it. It is the single line whose account is *not* a
 * revenue or expense account: every other line zeroes a P&L account, and the
 * last one parks the difference in equity. Identifying it by position would be
 * fragile; identifying it by "the account that is not being zeroed" is what it
 * actually is.
 *
 * If no such line can be identified, the preview still renders in full — only
 * the result summary is withheld, rather than a number being invented for it.
 */
const RESULT_LINE_PREFIXES = ['4', '5'];

const isProfitAndLossAccount = (code: string): boolean =>
    RESULT_LINE_PREFIXES.some((prefix) => code.startsWith(prefix));

/**
 * `ClosingEntryPreviewResponse` → the `ClosePreview` the dialog renders.
 *
 * The server sends lines and totals; the entry around them is assembled here.
 * Three things are carried through untouched rather than recomputed:
 *
 * - the **totals** are the server's `totalDebitTetri` / `totalCreditTetri`, not
 *   a sum of the lines, so a line this mapping had to drop cannot silently
 *   change a total;
 * - the **date** is the server's, and it is the date the commit will use —
 *   provided no `{ date }` body is sent, which is why the service never sends
 *   one;
 * - the **result figure** is the balancing line's own amount, read off the
 *   line rather than derived from the P&L.
 */
export const mapClosePreview = (
    wire: ClosingEntryPreviewWire,
): ClosePreview | null => {
    const rawLines = wire.lines ?? [];
    if (rawLines.length === 0) {
        return null;
    }

    const lines: JournalLine[] = rawLines.map((line) => {
        const debitTetri = line.debitTetri ?? 0;
        const creditTetri = line.creditTetri ?? 0;
        return {
            accountId: line.accountId ?? '',
            accountCode: line.accountCode,
            accountName: line.accountName,
            // The journal's own echo, not `formatGel`: these lines are rendered
            // by the journal's line table, and a preview formatted differently
            // from the entry it becomes would read as a different document.
            debit: decimalGelOf(debitTetri),
            debitTetri,
            credit: decimalGelOf(creditTetri),
            creditTetri,
            memo: '',
        };
    });

    const totalDebitTetri = wire.totalDebitTetri ?? 0;
    const totalCreditTetri = wire.totalCreditTetri ?? 0;

    const resultIndex = rawLines.findIndex(
        (line) => !isProfitAndLossAccount(line.accountCode ?? ''),
    );
    const resultLine = resultIndex >= 0 ? rawLines[resultIndex] : undefined;

    const entry: JournalEntry = {
        // Synthetic and deliberately unfetchable: nothing has been written.
        id: `preview:${wire.year ?? ''}`,
        entryNumber: null,
        date: wire.date ?? '',
        description: `Year-end close ${wire.year ?? ''}`,
        documentRef: '',
        status: 'DRAFT',
        source: 'CLOSING',
        lines,
        validationState: {
            isPostable: true,
            issues: [],
            totalDebitTetri,
            totalCreditTetri,
            differenceTetri: totalDebitTetri - totalCreditTetri,
        },
        etag: '',
        dateInClosedPeriod: false,
    };

    return {
        entry,
        resultTetri:
            (resultLine?.debitTetri ?? 0) + (resultLine?.creditTetri ?? 0),
        targetAccountId: resultLine?.accountId ?? '',
    };
};

// ── refusals ────────────────────────────────────────────────────────────────

/**
 * The fiscal-year endpoints send **no machine-readable code** — every refusal is
 * `{ error: "<English prose>" }`, with neither the `code` field the journal and
 * tax-settings endpoints populate nor the `errors.reason` map this client's
 * original contract assumed.
 *
 * So there is nothing to branch on and nothing to localise: a refusal that slips
 * past the client's own gates is shown in the server's words. Every refusal the
 * screen can *anticipate* is stated up front as a disabled reason in
 * `periodsMeta`, in the user's language — the prose fallback exists for races
 * and for rules this client does not model, not as the primary channel.
 *
 * Kept as a named export so the intent is greppable and the day a `code` lands
 * there is one place to bind it.
 */
export const PERIOD_REFUSALS_CARRY_NO_CODE = true;
